/**
 * Assistant — Hauptkomponente des Web-Chat-Widgets.
 *
 * Adaptiert aus dem Claude-Design-Bundle (Assistant.jsx). Phase 1:
 * Welcome-Screen mit Quick-Actions & Suggestions, Conversation-Screen
 * mit Streaming-Antworten, Composer.
 *
 * Phase 2: Tools (navigate_to_page, capture_lead), Voice-Input, Attach.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiClient, WidgetConfig, getOrCreateVisitorToken } from './api';

interface Props {
  widgetId: string;
  apiBase: string;
  initialLanguage?: string;
  accentOverride?: string;
  onNavigate: (path: string) => void;
}

type View = 'welcome' | 'convo';

interface ToolEvent {
  name: string;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  toolEvents?: ToolEvent[];
}

export function Assistant({ widgetId, apiBase, initialLanguage, accentOverride, onNavigate }: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('welcome');
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [language, setLanguage] = useState<string>(initialLanguage || 'de');
  const [hintOpen, setHintOpen] = useState(true);
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiRef = useRef<ApiClient | null>(null);
  const visitorTokenRef = useRef<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Init API + load config
  useEffect(() => {
    visitorTokenRef.current = getOrCreateVisitorToken();
    apiRef.current = new ApiClient({ apiBase, publicId: widgetId, visitorToken: visitorTokenRef.current });
    apiRef.current.getConfig(language)
      .then((c) => {
        setConfig(c);
        if (c.locale) setLanguage(c.locale.language);
        setHintOpen(c.widget.showHint);
      })
      .catch((e: unknown) => setError((e as Error).message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload config wenn Sprache wechselt
  useEffect(() => {
    if (!apiRef.current || !config) return;
    apiRef.current.getConfig(language).then(setConfig).catch(() => { /* fallback: keep old */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Auto-scroll bei neuen Messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);

  // Mobile-Optimierung: Body-Scroll sperren wenn Modal offen ist, damit
  // der User nicht versehentlich die Host-Page scrollt waehrend er chattet.
  // Plus: visualViewport-Listener fuer iOS-Tastatur — wenn die Tastatur
  // aufgeht, schrumpft visualViewport.height und wir setzen das Modal
  // dynamisch auf diese Hoehe damit der Composer sichtbar bleibt.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Visual-Viewport: nur wenn Mobile (< 600px) — Desktop braucht's nicht.
    const isMobile = window.matchMedia('(max-width: 600px)').matches;
    let detachVv: (() => void) | null = null;
    if (isMobile && window.visualViewport) {
      const vv = window.visualViewport;
      const onResize = () => {
        // Setze CSS-Variable die das Modal nutzen kann
        document.documentElement.style.setProperty('--gwd-vh', `${vv.height}px`);
      };
      onResize();
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
      detachVv = () => {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
        document.documentElement.style.removeProperty('--gwd-vh');
      };
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      if (detachVv) detachVv();
    };
  }, [open]);

  const accentVar = accentOverride || config?.widget.accentColor || '#C13038';

  async function ensureSession(): Promise<string | null> {
    if (sessionId) return sessionId;
    if (!apiRef.current) return null;
    try {
      const sid = await apiRef.current.createSession(language);
      setSessionId(sid);
      return sid;
    } catch (e) {
      setError((e as Error).message);
      return null;
    }
  }

  async function sendMessage(text: string) {
    if (!text.trim() || !apiRef.current) return;
    setError(null);
    const sid = await ensureSession();
    if (!sid) return;
    setView('convo');
    const userMsg: Message = { role: 'user', content: text };
    const assistantMsg: Message = { role: 'assistant', content: '', isStreaming: true, toolEvents: [] };
    setMsgs((prev) => [...prev, userMsg, assistantMsg]);
    setBusy(true);
    try {
      let acc = '';
      const collectedTools: ToolEvent[] = [];
      await apiRef.current.streamMessage(sid, text, language, {
        delta: (d) => {
          acc += d;
          setMsgs((prev) => {
            const copy = [...prev];
            const lastIdx = copy.length - 1;
            if (copy[lastIdx]?.role === 'assistant') {
              copy[lastIdx] = { ...copy[lastIdx]!, content: acc };
            }
            return copy;
          });
        },
        tool: (ev) => {
          collectedTools.push(ev);
          setMsgs((prev) => {
            const copy = [...prev];
            const lastIdx = copy.length - 1;
            if (copy[lastIdx]?.role === 'assistant') {
              copy[lastIdx] = { ...copy[lastIdx]!, toolEvents: [...(copy[lastIdx]!.toolEvents ?? []), ev] };
            }
            return copy;
          });
          // Side-Effects: navigate_to_page direkt ausloesen.
          if (ev.name === 'navigate_to_page' && ev.output?.ok) {
            const path = String(ev.output.path ?? '');
            if (path) {
              // Kurzer delay damit der User die Bestaetigung sieht.
              setTimeout(() => onNavigate(path), 600);
            }
          }
        },
        done: () => {
          setMsgs((prev) => {
            const copy = [...prev];
            const lastIdx = copy.length - 1;
            if (copy[lastIdx]?.role === 'assistant') {
              copy[lastIdx] = { ...copy[lastIdx]!, isStreaming: false };
            }
            return copy;
          });
        },
        error: (msg) => {
          setError(msg);
          setMsgs((prev) => prev.slice(0, -1));
        },
      });
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!input.trim()) return;
    const t = input.trim();
    setInput('');
    void sendMessage(t);
  }

  function reset() {
    setView('welcome');
    setMsgs([]);
    setSessionId(null);
    setError(null);
  }

  // ---------- Render ----------
  if (!config) {
    return null; // still loading or errored — silent until connected
  }

  return (
    <div className="gwd-chat-wrapper" style={{ '--gwd-accent': accentVar } as React.CSSProperties}>
      {/* Hint-Bubble */}
      {hintOpen && !open && config.locale?.hintText && (
        <div className="gwd-assist-hint">
          <span>{config.locale.hintText}</span>
          <span className="x" onClick={(e) => { e.stopPropagation(); setHintOpen(false); }}>×</span>
        </div>
      )}

      {/* Floating Dock */}
      {!open && (
        <button className="gwd-assist-dock" onClick={() => { setOpen(true); setHintOpen(false); }}>
          <span className="brand-mark" style={{ background: accentVar }}>G</span>
          <span className="placeholder">{config.locale?.composerPlaceholder || 'Wie kann ich helfen?'}</span>
          <span className="send-hint">→</span>
        </button>
      )}

      {/* Modal */}
      {open && (
        <>
          <div className="gwd-assist-backdrop" onClick={() => setOpen(false)} />
          <div className="gwd-assist-modal" role="dialog">
            <div className="gwd-assist-head" style={{ borderBottomColor: accentVar }}>
              <div className="avatar" style={{ background: accentVar }}>G</div>
              <div className="who">
                <div className="name">{config.widget.name}</div>
                <div className="status"><span className="dot" />Online</div>
              </div>
              {config.widget.availableLanguages.length > 1 && (
                <div className="lang-toggle">
                  {config.widget.availableLanguages.map((l) => (
                    <button key={l} className={l === language ? 'active' : ''} onClick={() => setLanguage(l)}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              {view === 'convo' && (
                <button className="head-btn" onClick={reset} title="Zurueck zum Start">⌂</button>
              )}
              <button className="head-btn" onClick={() => setOpen(false)} title="Schliessen">×</button>
            </div>

            <div className="gwd-assist-body" ref={scrollRef}>
              {view === 'welcome'
                ? <Welcome config={config} onPickPrompt={(p) => sendMessage(p)} />
                : <Conversation msgs={msgs} busy={busy} />}
              {error && <div className="gwd-error">⚠ {error}</div>}
            </div>

            <form className="gwd-assist-foot" onSubmit={handleSubmit}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={config.locale?.composerPlaceholder || 'Frage stellen…'}
                disabled={busy}
              />
              <button type="submit" className="send" disabled={!input.trim() || busy} style={{ background: input.trim() ? accentVar : undefined }}>
                ➤
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function Welcome({ config, onPickPrompt }: { config: WidgetConfig; onPickPrompt: (prompt: string) => void }) {
  const l = config.locale;
  if (!l) return null;
  return (
    <div className="gwd-assist-welcome">
      {l.welcomeTitle && <div className="greeting">{l.welcomeTitle}</div>}
      {l.welcomeSubtitle && <div className="sub">{l.welcomeSubtitle}</div>}

      {l.quickActions.length > 0 && (
        <>
          <div className="eyebrow">Schnellzugriff</div>
          <div className="gwd-qa-list">
            {l.quickActions.map((q) => (
              <button key={q.key} className="gwd-qa" onClick={() => onPickPrompt(q.prompt || q.label)}>
                <span className="copy">
                  <div className="t">{q.label}</div>
                  {q.description && <div className="d">{q.description}</div>}
                </span>
                <span className="arr">→</span>
              </button>
            ))}
          </div>
        </>
      )}

      {l.suggestions.length > 0 && (
        <>
          <div className="eyebrow">Häufige Fragen</div>
          <div className="gwd-sugs">
            {l.suggestions.map((s, i) => (
              <button key={i} className="gwd-sug" onClick={() => onPickPrompt(s)}>{s}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Conversation({ msgs, busy }: { msgs: Message[]; busy: boolean }) {
  return (
    <div className="gwd-msgs">
      {msgs.map((m, i) => (
        <div key={i} className={'gwd-msg ' + m.role}>
          <div className="av">{m.role === 'user' ? 'DU' : 'AI'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
            <div className="gwd-bubble">
              {m.content}
              {m.isStreaming && m.content === '' && <span className="gwd-typing"><span /><span /><span /></span>}
              {m.isStreaming && m.content && <span className="gwd-cursor">▍</span>}
            </div>
            {m.toolEvents && m.toolEvents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
                {m.toolEvents.map((ev, j) => <ToolBadge key={j} event={ev} />)}
              </div>
            )}
          </div>
        </div>
      ))}
      {busy && msgs.length === 0 && (
        <div className="gwd-msg bot">
          <div className="av">AI</div>
          <div className="gwd-bubble"><span className="gwd-typing"><span /><span /><span /></span></div>
        </div>
      )}
    </div>
  );
}

function ToolBadge({ event }: { event: ToolEvent }) {
  const ok = event.output?.ok !== false && !event.output?.error;
  let label = event.name;
  let detail = '';
  if (event.name === 'navigate_to_page') {
    label = 'Navigation';
    detail = `→ ${String(event.output?.path ?? event.input?.path ?? '?')}`;
  } else if (event.name === 'capture_lead') {
    label = 'Anfrage erfasst';
    if (ok && event.output?.leadId) detail = `· #${String(event.output.leadId).slice(0, 8)}`;
  }
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 11,
        background: ok ? '#e8f5ec' : '#fde8e9',
        color: ok ? '#166534' : '#9c242b',
        border: '1px solid ' + (ok ? '#c8e6d2' : '#f4dcde'),
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}
    >
      {ok ? '✓' : '×'} {label} {detail}
      {!ok && event.output?.error && <span style={{ marginLeft: 4 }}>· {String(event.output.error)}</span>}
    </div>
  );
}
